import { Html5Qrcode } from 'html5-qrcode';
import { OfflineStore } from './offlineStore';

// Escáner de boletos para /events/:id/checkin.
// - Lee el QR (uid propio o folio externo) y lo valida en el servidor.
// - Sin red, resuelve contra la copia local (IndexedDB) y encola el escaneo para sincronizar.
// - Cada validación lleva puerta (categorías admitidas) y dispositivo.

const SCAN_PAUSE_MS = 1500;
const RESULT_HIDE_MS = 7000;
const STATS_POLL_MS = 10000;
const SYNC_INTERVAL_MS = 30000;
const SYNC_BATCH = 50;

const MESSAGES = {
  ok: 'Acceso permitido',
  already_used: 'Boleto ya utilizado',
  not_paid: 'Boleto sin pagar',
  invalidated: 'Boleto invalidado',
  transferred: 'Boleto transferido: este QR ya no es válido',
  wrong_gate: 'Categoría de otra puerta',
  not_found: 'Boleto no encontrado en este evento',
  conflict: 'Ya lo registró otro dispositivo',
  error: 'Error al validar',
};

const TONE = { ok: 'success', already_used: 'danger', wrong_gate: 'warning', conflict: 'warning' };

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;
const el = (root, target) => root.querySelector(`[data-checkin-target="${target}"]`);
const timeLabel = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : null);

// --- Sonido y vibración ------------------------------------------------------
let audioContext = null;
const beep = (pattern) => {
  try {
    audioContext ||= new (window.AudioContext || window.webkitAudioContext)();
    let at = audioContext.currentTime;
    pattern.forEach(([freq, ms]) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(audioContext.destination);
      gain.gain.setValueAtTime(0.2, at);
      osc.start(at);
      osc.stop(at + ms / 1000);
      at += ms / 1000 + 0.06;
    });
  } catch (_error) {
    // Sin audio (permisos o navegador): la vibración y el color bastan.
  }
};

const feedback = (result) => {
  if (result === 'ok') {
    beep([[880, 120]]);
    if (navigator.vibrate) navigator.vibrate(80);
  } else if (TONE[result] === 'warning') {
    beep([[520, 260]]);
    if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
  } else {
    beep([[220, 180], [220, 180]]);
    if (navigator.vibrate) navigator.vibrate([200, 80, 200]);
  }
};

// --- Preferencias locales (puerta y dispositivo) ----------------------------
const deviceKey = 'talon.checkin.device';
const gateKey = (eventId) => `talon.checkin.gate.${eventId}`;

const loadDevice = () => {
  const stored = JSON.parse(localStorage.getItem(deviceKey) || 'null');
  if (stored?.id) return stored;
  const device = { id: Math.random().toString(36).slice(2, 8).toUpperCase(), name: '' };
  localStorage.setItem(deviceKey, JSON.stringify(device));
  return device;
};

const deviceLabel = (device) => (device.name ? `${device.name} (${device.id})` : device.id);

class CheckinScreen {
  constructor(root) {
    this.root = root;
    this.eventId = root.dataset.eventId;
    this.prices = JSON.parse(root.dataset.prices || '[]');
    this.device = loadDevice();
    this.gate = JSON.parse(localStorage.getItem(gateKey(this.eventId)) || 'null');
    this.store = OfflineStore.supported() ? new OfflineStore(this.eventId) : null;
    this.scanner = null;
    this.paused = false;
    this.timers = [];
    this.hideTimer = null;
    this.pendingOverride = null;
  }

  // --- Ciclo de vida ---------------------------------------------------------
  async mount() {
    // Solo lectura: ve los ingresos, sin cámara ni registro de entradas.
    if (this.root.dataset.readonly === 'true') {
      this.renderDevice();
      this.updateStatus();
      this.timers.push(setInterval(() => this.pollStats(), STATS_POLL_MS));
      return;
    }

    this.bindSetup();
    this.bindSearch();
    this.renderGate();
    this.renderDevice();
    this.updateStatus();

    window.addEventListener('online', this.onOnline = () => { this.updateStatus(); this.sync(); });
    window.addEventListener('offline', this.onOffline = () => this.updateStatus());

    if (!this.gate) this.openSetup();

    this.startScanner();
    this.timers.push(setInterval(() => this.pollStats(), STATS_POLL_MS));
    this.timers.push(setInterval(() => this.sync(), SYNC_INTERVAL_MS));
    this.sync();
  }

  async unmount() {
    this.timers.forEach(clearInterval);
    clearTimeout(this.hideTimer);
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    await this.stopScanner();
  }

  // --- Cámara ----------------------------------------------------------------
  startScanner() {
    const reader = this.root.querySelector('#checkin-reader');
    if (!reader) return;

    this.scanner = new Html5Qrcode('checkin-reader');
    this.scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: (w, h) => { const s = Math.min(w, h) * 0.8; return { width: s, height: s }; } },
        (decodedText) => this.onScan(decodedText),
      )
      .catch(() => {
        reader.classList.add('checkin__reader--off');
        reader.textContent = 'No se pudo abrir la cámara. Busca el boleto por código o nombre.';
      });
  }

  async stopScanner() {
    if (!this.scanner) return;
    try {
      if (this.scanner.isScanning) await this.scanner.stop();
      this.scanner.clear();
    } catch (_error) {
      // Ya estaba detenido
    }
    this.scanner = null;
  }

  onScan(text) {
    if (this.paused) return;
    this.paused = true;
    this.validate(text.trim()).finally(() => {
      setTimeout(() => { this.paused = false; }, SCAN_PAUSE_MS);
    });
  }

  // --- Validación ------------------------------------------------------------
  payload(code, force) {
    return {
      uid: code,
      gate: this.gate?.label || null,
      device: deviceLabel(this.device),
      allowed_price_ids: this.gate?.priceIds || [],
      force: Boolean(force),
    };
  }

  async validate(code, { force = false } = {}) {
    if (!code) return;

    if (navigator.onLine) {
      try {
        const response = await fetch(this.root.dataset.checkinUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
          body: JSON.stringify(this.payload(code, force)),
        });
        if (response.status === 401 || response.redirected) {
          window.location.reload();
          return;
        }
        const data = await response.json();
        this.showResult(data, { code });
        if (data.stats) this.renderStats(data.stats);
        if (this.store && data.result === 'ok' && data.ticket) {
          await this.store.markUsed(data.ticket.uid, { at: new Date().toISOString(), gate: this.gate?.label, device: deviceLabel(this.device) });
        }
        return;
      } catch (_error) {
        // Sin red: seguimos con la copia local.
      }
    }

    await this.validateOffline(code, force);
  }

  async validateOffline(code, force) {
    if (!this.store) {
      this.showResult({ result: 'error', message: 'Sin conexión. Intenta de nuevo.' }, { code });
      return;
    }

    const { ticket, via } = await this.store.resolve(code);
    let result;
    if (!ticket) result = 'not_found';
    else if (via === 'old') result = 'transferred';
    else if (ticket.status !== 'completed') result = 'not_paid';
    else if (ticket.invalidated) result = 'invalidated';
    else if (ticket.checkedInAt) result = 'already_used';
    else if (this.gate?.priceIds?.length && !force && !this.gate.priceIds.includes(ticket.priceId)) result = 'wrong_gate';
    else result = 'ok';

    if (result === 'ok') {
      const at = new Date().toISOString();
      await this.store.markUsed(ticket.uid, { at, gate: this.gate?.label, device: deviceLabel(this.device) });
      await this.store.enqueue({ uid: ticket.uid, gate: this.gate?.label || null, device: deviceLabel(this.device), scanned_at: at, force: Boolean(force) });
      this.bumpLocalStats(ticket.priceId);
    }

    this.showResult({
      result,
      message: MESSAGES[result],
      offline: true,
      ticket: ticket && {
        uid: ticket.uid,
        name: ticket.name,
        price_id: ticket.priceId,
        category: this.prices.find((p) => p.id === ticket.priceId)?.name || '',
        checked_in_at: timeLabel(ticket.checkedInAt),
        checked_in_gate: ticket.gate,
        checked_in_device: ticket.device,
      },
    }, { code });
    this.updateStatus();
  }

  // --- Resultado en pantalla -------------------------------------------------
  canOverride(ticket) {
    if (!this.gate?.priceIds?.length || !ticket) return false;
    const ticketPrice = this.prices.find((p) => p.id === ticket.price_id)?.price ?? 0;
    const gateMin = Math.min(...this.gate.priceIds.map((id) => this.prices.find((p) => p.id === id)?.price ?? 0));
    // VIP puede entrar por General; General no entra por VIP.
    return ticketPrice >= gateMin;
  }

  showResult(data, { code } = {}) {
    const box = el(this.root, 'result');
    const tone = TONE[data.result] || (data.result === 'ok' ? 'success' : 'danger');
    clearTimeout(this.hideTimer);

    box.hidden = false;
    box.className = `checkin__result checkin__result--${tone}`;
    box.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'checkin__result-title';
    title.textContent = data.message || MESSAGES[data.result] || MESSAGES.error;
    box.appendChild(title);

    if (data.ticket) {
      const name = document.createElement('div');
      name.className = 'checkin__result-name';
      name.textContent = data.ticket.name || 'Sin nombre';
      box.appendChild(name);

      const category = document.createElement('div');
      category.className = 'checkin__result-category';
      category.textContent = [data.ticket.category, data.ticket.source].filter(Boolean).join(' · ');
      box.appendChild(category);

      const details = [];
      if (data.result === 'already_used' || data.result === 'conflict') {
        details.push(`Entró a las ${data.ticket.checked_in_at || '—'}`);
        if (data.ticket.checked_in_gate) details.push(`puerta ${data.ticket.checked_in_gate}`);
        if (data.ticket.checked_in_device) details.push(`dispositivo ${data.ticket.checked_in_device}`);
      }
      if (data.result === 'transferred' && data.ticket.transferred_to) details.push(`Ahora es de ${data.ticket.transferred_to}`);
      if (data.result === 'wrong_gate' && this.gate) details.push(`Esta puerta: ${this.gate.label}`);
      if (details.length) {
        const detail = document.createElement('div');
        detail.className = 'checkin__result-detail';
        detail.textContent = details.join(' · ');
        box.appendChild(detail);
      }

      if (data.result === 'wrong_gate' && this.canOverride(data.ticket)) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-light checkin__override';
        button.textContent = 'Admitir de todos modos';
        button.addEventListener('click', () => this.validate(code || data.ticket.uid, { force: true }));
        box.appendChild(button);
      }
    }

    if (data.offline) {
      const badge = document.createElement('div');
      badge.className = 'checkin__result-offline';
      badge.textContent = 'Validado sin conexión';
      box.appendChild(badge);
    }

    feedback(data.result);
    this.hideTimer = setTimeout(() => { box.hidden = true; }, RESULT_HIDE_MS);
  }

  // --- Contadores ------------------------------------------------------------
  renderStats(stats) {
    el(this.root, 'count').textContent = stats.checked_in_count;
    el(this.root, 'sold').textContent = stats.sold_count;
    const list = el(this.root, 'categories');
    stats.categories.forEach((cat) => {
      const item = list.querySelector(`[data-price-id="${cat.id}"]`);
      if (item) {
        item.querySelector('b').textContent = cat.checked_in;
        item.lastChild.textContent = `/${cat.sold}`;
      }
    });
  }

  bumpLocalStats(priceId) {
    const count = el(this.root, 'count');
    count.textContent = Number(count.textContent || 0) + 1;
    const item = el(this.root, 'categories').querySelector(`[data-price-id="${priceId}"] b`);
    if (item) item.textContent = Number(item.textContent || 0) + 1;
  }

  async pollStats() {
    if (!navigator.onLine) return;
    try {
      const response = await fetch(this.root.dataset.statsUrl, { headers: { Accept: 'application/json' } });
      if (response.ok) this.renderStats(await response.json());
    } catch (_error) {
      // Sin red; el siguiente intento lo reintenta.
    }
  }

  // --- Estado de conexión y sincronización ----------------------------------
  async updateStatus() {
    const status = el(this.root, 'status');
    const pending = this.store ? await this.store.queueSize() : 0;
    if (!navigator.onLine) {
      status.dataset.state = 'offline';
      status.textContent = pending ? `Sin conexión · ${pending} por sincronizar` : 'Sin conexión';
    } else if (pending) {
      status.dataset.state = 'syncing';
      status.textContent = `Sincronizando ${pending}…`;
    } else {
      status.dataset.state = 'online';
      const local = this.store ? await this.store.count() : 0;
      status.textContent = local ? `En línea · ${local} boletos en el dispositivo` : 'En línea';
    }
  }

  async sync() {
    if (!this.store || !navigator.onLine || this.syncing) return;
    this.syncing = true;
    try {
      await this.pushQueue();
      await this.pullManifest();
    } finally {
      this.syncing = false;
      this.updateStatus();
    }
  }

  async pushQueue() {
    const syncUrl = this.root.dataset.syncUrl;
    if (!syncUrl) return;

    for (;;) {
      const batch = await this.store.queued(SYNC_BATCH);
      if (!batch.length) return;

      const response = await fetch(syncUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ scans: batch.map(({ uid, gate, device, scanned_at, force }) => ({ uid, gate, device, scanned_at, force })) }),
      });
      if (!response.ok) return;

      const data = await response.json();
      await this.store.dequeue(batch.map((item) => item.id));
      (data.results || []).filter((r) => r.result === 'conflict').forEach((r) => this.addConflict(r));
      if (data.stats) this.renderStats(data.stats);
      if (batch.length < SYNC_BATCH) return;
    }
  }

  async pullManifest() {
    const manifestUrl = this.root.dataset.manifestUrl;
    if (!manifestUrl) return;

    const since = await this.store.getMeta('since');
    const url = since ? `${manifestUrl}?since=${encodeURIComponent(since)}` : manifestUrl;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    const data = await response.json();
    if (data.tickets?.length) await this.store.upsertTickets(data.tickets);
    if (data.generated_at) await this.store.setMeta('since', data.generated_at);
  }

  addConflict(entry) {
    const section = el(this.root, 'conflicts');
    const list = el(this.root, 'conflictList');
    const item = document.createElement('li');
    const t = entry.ticket || {};
    item.textContent = `${t.name || entry.uid} (${t.category || ''}): también entró a las ${t.checked_in_at || '—'} por ${t.checked_in_device || 'otro dispositivo'}`;
    list.appendChild(item);
    section.hidden = false;
  }

  // --- Puerta y dispositivo --------------------------------------------------
  bindSetup() {
    el(this.root, 'gateButton').addEventListener('click', () => this.openSetup());
    el(this.root, 'saveGate').addEventListener('click', () => this.saveSetup());
  }

  openSetup() {
    const setup = el(this.root, 'setup');
    setup.hidden = false;
    setup.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = Boolean(this.gate?.priceIds?.includes(Number(input.value)));
    });
    el(this.root, 'deviceInput').value = this.device.name || '';
  }

  saveSetup() {
    const checked = [...el(this.root, 'gateOptions').querySelectorAll('input:checked')];
    const priceIds = checked.map((input) => Number(input.value));
    const label = checked.length ? checked.map((input) => input.dataset.name).join(' + ') : 'Todas';
    this.gate = { priceIds, label };
    localStorage.setItem(gateKey(this.eventId), JSON.stringify(this.gate));

    this.device.name = el(this.root, 'deviceInput').value.trim();
    localStorage.setItem(deviceKey, JSON.stringify(this.device));

    el(this.root, 'setup').hidden = true;
    this.renderGate();
    this.renderDevice();
  }

  renderGate() {
    el(this.root, 'gateButton').textContent = this.gate ? `Puerta: ${this.gate.label}` : 'Elegir puerta';
  }

  renderDevice() {
    el(this.root, 'deviceLabel').textContent = `Dispositivo ${deviceLabel(this.device)}`;
  }

  // --- Búsqueda manual -------------------------------------------------------
  bindSearch() {
    const form = el(this.root, 'form');
    const input = el(this.root, 'searchInput');
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const query = input.value.trim();
      if (!query) return;
      const looksLikeCode = !/\s/.test(query) && !query.includes('@') && query.length >= 6;
      if (looksLikeCode) {
        this.validate(query);
        input.value = '';
        el(this.root, 'searchResults').hidden = true;
      } else {
        this.search(query);
      }
    });
  }

  async search(query) {
    let results = [];
    if (navigator.onLine) {
      try {
        const response = await fetch(`${this.root.dataset.searchUrl}?q=${encodeURIComponent(query)}`, { headers: { Accept: 'application/json' } });
        if (response.ok) results = (await response.json()).results;
      } catch (_error) {
        results = [];
      }
    } else if (this.store) {
      results = (await this.store.search(query)).map((t) => ({
        uid: t.uid, name: t.name, category: this.prices.find((p) => p.id === t.priceId)?.name || '',
        checked_in: Boolean(t.checkedInAt), checked_in_at: timeLabel(t.checkedInAt), status: t.status, invalidated: t.invalidated,
      }));
    }
    this.renderSearch(results);
  }

  renderSearch(results) {
    const list = el(this.root, 'searchResults');
    list.innerHTML = '';
    list.hidden = false;

    if (!results.length) {
      const empty = document.createElement('li');
      empty.className = 'checkin__search-empty';
      empty.textContent = 'Sin resultados';
      list.appendChild(empty);
      return;
    }

    results.forEach((ticket) => {
      const item = document.createElement('li');
      const info = document.createElement('div');
      info.innerHTML = `<strong></strong><br><small></small>`;
      info.querySelector('strong').textContent = ticket.name || 'Sin nombre';
      const state = ticket.checked_in ? `Entró ${ticket.checked_in_at || ''}` : (ticket.invalidated ? 'Invalidado' : 'Sin usar');
      info.querySelector('small').textContent = `${ticket.category} · ${ticket.uid}${ticket.external_code ? ` · ${ticket.external_code}` : ''} · ${state}`;
      item.appendChild(info);

      if (!ticket.checked_in && !ticket.invalidated && ticket.status === 'completed') {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-publish btn-sm';
        button.textContent = 'Admitir';
        button.addEventListener('click', () => {
          this.validate(ticket.uid, { force: true });
          list.hidden = true;
          el(this.root, 'searchInput').value = '';
        });
        item.appendChild(button);
      }
      list.appendChild(item);
    });
  }
}

// --- Montaje con Turbo ---------------------------------------------------------
let screen = null;

const registerServiceWorker = (url) => {
  if (!url || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register(url, { scope: '/' }).catch(() => undefined);
};

const mount = () => {
  const root = document.querySelector('[data-controller="checkin"]');
  const index = document.querySelector('[data-controller="checkin-index"]');
  registerServiceWorker(root?.dataset.swUrl || index?.dataset.swUrl);
  if (!root) return;

  screen = new CheckinScreen(root);
  screen.mount();
};

const unmount = async () => {
  if (!screen) return;
  const current = screen;
  screen = null;
  await current.unmount();
};

document.addEventListener('turbo:load', mount);
document.addEventListener('turbo:before-visit', unmount);
document.addEventListener('turbo:before-cache', unmount);
